import { FC } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { InternalOverviewResponse } from '../../types/internal';
import { InternalKPICard, InternalChartCard, InternalSectionHeader } from './index';
import { formatNumber, tooltipStyle } from '../../utils/formatters';

interface InternalSalesSectionProps {
    leads: InternalOverviewResponse['leads'];
    bookings: InternalOverviewResponse['bookings'];
    emails: InternalOverviewResponse['emails'];
    periodLabel: string;
    periodLabelFull: string;
}

export const InternalSalesSection: FC<InternalSalesSectionProps> = ({ leads, bookings, emails, periodLabel, periodLabelFull }) => {
    const leadsByStatusData = (leads.byStatus || []).filter(s => s.count > 0).slice(0, 6);

    // Unified activity trend data (aligned by date)
    const allDates = Array.from(new Set([
        ...(leads.trend || []).map(d => d.date),
        ...(bookings.trend || []).map(d => d.date),
        ...(emails.trend || []).map(d => d.date)
    ])).sort();

    const activityTrendData = allDates.map(dateStr => {
        const lead = (leads.trend || []).find(d => d.date === dateStr);
        const booking = (bookings.trend || []).find(d => d.date === dateStr);
        const emailTrend = (emails.trend || []).find(d => d.date === dateStr);

        return {
            date: format(new Date(dateStr), 'MMM d'),
            leads: lead?.count || 0,
            bookings: booking?.count || 0,
            emails: emailTrend?.count || 0,
        };
    });

    return (
        <div className="space-y-4">
            <section>
                <InternalSectionHeader title="Sales Pipeline" />
                <div className="grid grid-cols-6 gap-3">
                    <InternalKPICard label="Total Leads" value={formatNumber(leads.total ?? 0)} sublabelText="All time" tooltip="Accumulated count of leads from all time." />
                    <InternalKPICard label={`Leads (${periodLabel})`} value={formatNumber(leads.inRange ?? 0)} sublabelText="Period" tooltip="Leads acquired within the selected period." />
                    <InternalKPICard label="Conversion" value={leads.conversionRate || '0%'} sublabelText="To booking" tooltip="Percentage of leads that converted into bookings." />
                    <InternalKPICard label="Total Bookings" value={formatNumber(bookings.total ?? 0)} sublabelText="All time" tooltip="Accumulated count of bookings from all time." />
                    <InternalKPICard label={`Bookings (${periodLabel})`} value={formatNumber(bookings.inRange ?? 0)} sublabelText="Period" tooltip="Bookings made within the selected period." />
                    <InternalKPICard label="Upcoming" value={formatNumber(bookings.upcoming ?? 0)} sublabelText="Scheduled" tooltip="Total number of upcoming scheduled bookings." />
                </div>
            </section>

            <section className="grid grid-cols-3 gap-3">
                {/* Activity Trend */}
                <InternalChartCard title="Activity Trend" subtitle={`Leads & bookings, ${periodLabelFull}`} className="col-span-2" description="Daily trend of new leads and bookings.">
                    <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={activityTrendData.slice(-14)} margin={{ top: 5, right: 5, left: -15, bottom: 0 }} barGap={1}>
                                <CartesianGrid stroke="#e5e5e5" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Bar dataKey="leads" fill="#a3a3a3" radius={0} name="Leads" />
                                <Bar dataKey="bookings" fill="#262626" radius={0} name="Bookings" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex gap-4 mt-2 text-[10px]">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-neutral-400" /> Leads</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-neutral-800" /> Bookings</span>
                    </div>
                </InternalChartCard>

                {/* Leads by Status */}
                <InternalChartCard title="Leads by Status" subtitle="Current distribution" description="Current distribution of leads by their pipeline status.">
                    <div className="space-y-2">
                        {leadsByStatusData.length > 0 ? leadsByStatusData.map((s, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px]">
                                <span className="text-neutral-600 capitalize">{s.status?.toLowerCase().replace(/_/g, ' ') || 'Unknown'}</span>
                                <span className="font-medium text-neutral-900">{s.count}</span>
                            </div>
                        )) : <p className="text-[11px] text-neutral-400">No leads</p>}
                    </div>
                </InternalChartCard>
            </section>
        </div>
    );
};
